<script setup lang="ts">

import { ref, computed } from 'vue';
import { usePairingState } from './Types/UsePairingState';
import { PageDots } from "@app/Components/PageDots";
import { Loading } from '@app/Components/Layout';
import { isNotProduction } from '@modules/Utilities/Environments';
import { reloadMyGarageViewModel, useViewModelInstance } from '@app/MyGarage/Types/MyGarageViewModelStore';

import CarbucksLogo from '@app/Components/CarbucksLogo/CarbucksLogo.vue';
import SkipHealthMonitorConnectionMenu from './Components/SkipHealthMonitorConnectionMenu.vue';
import HealthMonitorDiagnostics from './Components/HealthMonitorDiagnostics.vue';
import { useRouter } from '@library/Services';

const pairing = usePairingState();
let model = await useViewModelInstance( pairing.isPairing );

const emit = defineEmits( [ 'reload' ] );
const page = ref( 0 );
const showSkipMenu = ref( false );
const isCheckingConnection = ref( false );
const isNeverPaired = computed( () => model.carePlan.healthMonitor.isPaired == false );
const showDebugButton = isNotProduction();
const showDebugDetails = ref( false );

/**
 * Checks the connection by reloading the MyGarageViewModel.  If the connection has been made, the
 * Health Monitor feature will now be in a state of paired and the page should switch over to the normal
 * MyGarage view
 */
async function checkConnection()
{

    isCheckingConnection.value = true;

    const start = new Date().getTime();

    await new Promise( resolve => { setTimeout( resolve, 1000 ) } );
    model = await reloadMyGarageViewModel();

    const remainer = 4000 - ( new Date().getTime() - start );
    if( remainer > 0 )
    {
        await new Promise( resolve => { setTimeout( resolve, remainer ) } );
    }

    pairing.hasCheckedAgain = true
    isCheckingConnection.value = false;

    if( model.showConnectToHealthMonitorWorkflow == false )
    {
        const router = useRouter();
        router.go( '/my-garage' );
    }

}

</script>

<template>

    <div class="flex flex-col h-full pb-6">

        <!-- LOGO -->
        <div class="text-center transition-all duration-300 ease-in
                    opacity-100 overflow-hidden -translate-y-2 scale-90">
            <div class="flex flex-col items-center">
                <CarbucksLogo :size="96" />
            </div>

            <Button v-if="showDebugButton"
                    variant="text"
                    class="!h-8 3xl:!h-11"
                    v-ripple="false"
                    @click="() => showDebugDetails = !showDebugDetails">
                Diagnostics
            </Button>   

        </div>
        
        <!-- USER HAD CLICKED THE CONNECT HEALTH MONITOR BUTTON, BUT THE VEHICLE IS STILL NOT PAIRED -->
        <div v-if="pairing.isPairing"
             class="flex-1 flex flex-col overflow-hidden">
        
            <div class="text base lg:text-lg 3xl:text-2xl text-white tracking-wider text-center">
                <span v-if="pairing.hasCheckedAgain">
                    The Health Monitor does<br/>not appear to be paired
                </span>
                <span v-else>
                    Verify the Health Monitor<br/>has been paired to your phone
                </span>
            </div>

            <div class="flex-1 flex flex-col items-center justify-center text-base 3xl:text-xl px-8">

                <div class="w-full flex justify-center pb-4 mb-6 3xl:mb-10">
                    <img src="@/assets/images/health-monitor/carbucks-obd-dark.png" class="w-2/3 mx-auto my-6 3xl:my-10" />
                    <div class="absolute flex flex-col items-center -bottom-2"
                         :class="isCheckingConnection || pairing.hasCheckedAgain == false ? 'text-secondary-500' : 'text-rose-600'">
                        <div class="rounded-full aspect-square p-1 z-10 mb-1 3xl:mb-3
                                    flex items-center justify-center bg-current">
                            <Icon :name="isCheckingConnection ? 'working' : 'disconnected'" 
                                  class="text-2xl 3xl:text-4xl text-white"
                            />
                        </div>
                        <div class="rounded-full uppercase pt-1.5 pb-2 px-4 lg:px-6
                                    text-current text-sm 3xl:text-lg font-bold">
                            <span v-if="isCheckingConnection">Checking Connection</span>
                            <span v-else-if="pairing.hasCheckedAgain">Not Paired</span>
                            <span v-else>Tap below to verify</span>
                        </div>
                    </div>
                </div>
        
                <div v-if="isCheckingConnection">
                    <Loading class="!h-12 3xl:!h-14 3xl:scale-125" />
                </div>

                <Button v-else-if="pairing.isPairing"
                        variant="tonal"
                        class="!h-12 3xl:!h-14"
                        @click="() => checkConnection()">
                    Check {{ pairing.hasCheckedAgain ? 'Again' : 'Connection' }}
                </Button>   

            </div>
        
            <div class="text base 3xl:text-2xl text-white tracking-wider text-center mt-4 lg:mt-6 3xl:mt-10
                        transition-all translate-y-4 opacity-0"
                 :class="{
                    '!translate-y-0 !opacity-100': pairing.isPairing && pairing.hasCheckedAgain
                 }">
                Tap below to try connecting again<br/>or seek help from a nearby staff member
            </div>
            
        </div>

        <!-- EXPLAIN HOW TO CONNECT THE HEALTH MONITOR -->
        <div v-else 
             class="flex-1 flex flex-col overflow-hidden">
        
            <div class="text base lg:text-lg 3xl:text-2xl text-white tracking-wider text-center">
                <span v-if="isNeverPaired">
                    Connect your phone to your car<br/>in 3 simple steps
                </span>
                <span v-else>
                    Health Monitor may be disconnected<br/>You need to reconnect to continue
                </span>
            </div>

            <TabPanels v-model="page" class="h-full">

                <TabPanel :value="0" class="h-full w-full pb-4">
                    <img src="@/assets/images/health-monitor/connect-health-monitor-step-1.png" class="h-full mx-auto pointer-events-none" />
                </TabPanel>

                <TabPanel :value="1"  class="h-full w-full pb-4">
                    <img src="@/assets/images/health-monitor/connect-health-monitor-step-2.png" class="h-full mx-auto pointer-events-none" />
                </TabPanel>

                <TabPanel :value="2" class="h-full w-full pb-4">
                    <img src="@/assets/images/health-monitor/connect-health-monitor-step-3.png" class="h-full mx-auto pointer-events-none" />
                </TabPanel>

            </TabPanels>

            <!-- PAGE X OF Y DOTS -->
            <div class="w-full flex justify-center">
                <PageDots v-model="page"
                        :count="3"
                        size="sm"
                        class="mb-5" />
            </div>

        </div>

        <div v-if="pairing.isPairing == false || ( pairing.isPairing && pairing.hasCheckedAgain )"
             class="flex flex-col gap-2 px-4 lg:px-6 3xl:px-10 mt-4 lg:mt-6 3xl:mt-8">

            <JupiterButton type="page"
                           color="secondary"
                           class="!h-12 3xl:!h-14"
                           to="vehiclesettings"
                           variant="flat"
                           @click="() => pairing.beginPairing()">
                Connect Health Monitor
            </JupiterButton> 
            
            <Button variant="text"
                    class="!h-11 3xl:!h-14"
                    v-ripple="false"
                    @click="() => showSkipMenu = true">
                Skip
            </Button>   

            <SkipHealthMonitorConnectionMenu :model="model" v-model="showSkipMenu" />   

        </div>

        <HealthMonitorDiagnostics :model="model" v-model="showDebugDetails" />

    </div>
    
</template>